import InsightsOutlinedIcon from "@mui/icons-material/InsightsOutlined";
import QueryStatsOutlinedIcon from "@mui/icons-material/QueryStatsOutlined";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Divider from "@mui/material/Divider";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { alpha } from "@mui/material/styles";
import { isApiError } from "@bw/shared/api";
import { useAuth } from "@bw/shared/hooks";
import { formatMoney } from "@bw/shared/utils";
import { useEffect, useRef, useState } from "react";
import { Link as RouterLink } from "react-router-dom";

import { useResolvedFacility } from "@/hooks/use-selected-facility";
import { useMyOrg } from "@/hooks/use-my-org";
import {
  compareFacilityOperatingPeriods,
  getFacilityDemandOutlook,
  type OperationalInsightsComparisonRequest,
  type OperationalInsightsComparisonResponse,
  type OperationalInsightsDemandOutlookResponse,
} from "@/libs/analyticsApi";

const utcDateBack = (days: number) => {
  const date = new Date();
  date.setUTCHours(0, 0, 0, 0);
  date.setUTCDate(date.getUTCDate() - days);
  return date.toISOString().slice(0, 10);
};

const asInstant = (date: string) => `${date}T00:00:00Z`;
const afterInclusiveDate = (date: string) => {
  const end = new Date(asInstant(date));
  end.setUTCDate(end.getUTCDate() + 1);
  return end.toISOString();
};
const dateProps = {
  fullWidth: true,
  size: "small" as const,
  slotProps: { inputLabel: { shrink: true } },
  type: "date",
};

const signed = (value: number, suffix = "") =>
  `${value > 0 ? "+" : ""}${new Intl.NumberFormat().format(value)}${suffix}`;

const money = (
  value: string | number | null | undefined,
  currency: string | null | undefined,
) => (value != null && currency ? formatMoney(value, currency) : "—");

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <Box>
      <Typography
        sx={{ fontVariantNumeric: "tabular-nums", fontWeight: 800 }}
        variant="h6"
      >
        {value}
      </Typography>
      <Typography color="text.secondary" variant="caption">
        {label}
      </Typography>
    </Box>
  );
}

function PeriodCard({
  period,
}: {
  period: OperationalInsightsComparisonResponse["focus"];
}) {
  return (
    <Paper component="section" variant="outlined" sx={{ p: 2.5 }}>
      <Stack spacing={2}>
        <Box>
          <Typography component="h2" sx={{ fontWeight: 800 }} variant="h6">
            {period.label}
          </Typography>
          <Typography color="text.secondary" variant="body2">
            {new Date(period.start).toLocaleDateString(undefined, {
              dateStyle: "medium",
              timeZone: "UTC",
            })}
            {" – "}
            {new Date(new Date(period.end).getTime() - 1).toLocaleDateString(
              undefined,
              {
                dateStyle: "medium",
                timeZone: "UTC",
              },
            )}
          </Typography>
        </Box>
        <Box
          sx={{
            display: "grid",
            gap: 2,
            gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
          }}
        >
          <Metric
            label="Confirmed court events"
            value={String(period.booking_count)}
          />
          <Metric
            label="Court utilization"
            value={`${period.utilization_percent}%`}
          />
          <Metric
            label={`Cancellations (${period.cancelled_bookings} of ${period.total_bookings})`}
            value={`${period.cancellation_rate}%`}
          />
          <Metric
            label="Gross revenue"
            value={money(period.gross_revenue, period.currency)}
          />
        </Box>
        <Divider />
        <Stack spacing={0.5}>
          <Typography sx={{ fontWeight: 700 }} variant="subtitle2">
            Demand signals
          </Typography>
          <Typography color="text.secondary" variant="body2">
            {period.busiest_court
              ? `${period.busiest_court.court_name}: ${period.busiest_court.booking_count} bookings`
              : "No busiest court in this window."}
          </Typography>
          <Typography color="text.secondary" variant="body2">
            {period.busiest_hour
              ? `${String(period.busiest_hour.hour_utc).padStart(2, "0")}:00 UTC: ${period.busiest_hour.booking_count} bookings`
              : "No peak hour in this window."}
          </Typography>
        </Stack>
        {period.revenue_sources.length ? (
          <Stack spacing={0.75}>
            <Typography sx={{ fontWeight: 700 }} variant="subtitle2">
              Revenue by service
            </Typography>
            {period.revenue_sources.map((source) => (
              <Stack
                direction="row"
                key={source.source}
                sx={{ justifyContent: "space-between" }}
              >
                <Typography
                  sx={{ textTransform: "capitalize" }}
                  variant="body2"
                >
                  {source.source.replace(/_/g, " ")}
                </Typography>
                <Typography variant="body2">
                  {money(source.revenue, period.currency)}
                </Typography>
              </Stack>
            ))}
          </Stack>
        ) : null}
      </Stack>
    </Paper>
  );
}

function DemandOutlookCard({
  outlook,
}: {
  outlook: OperationalInsightsDemandOutlookResponse;
}) {
  const recent = outlook.history.slice(-8);
  const maximum = Math.max(
    1,
    ...recent.map((week) => week.confirmed_court_events),
  );
  const targetLabel = new Intl.DateTimeFormat(undefined, {
    month: "short",
    day: "numeric",
    timeZone: "UTC",
  }).format(new Date(outlook.target_start));

  return (
    <Paper
      component="section"
      variant="outlined"
      sx={{
        background: (theme) =>
          `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)}, ${alpha(theme.palette.background.paper, 0)} 58%)`,
        overflow: "hidden",
        p: { xs: 2, sm: 2.5 },
      }}
    >
      <Stack spacing={2}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1}
          sx={{ alignItems: { sm: "center" }, justifyContent: "space-between" }}
        >
          <Box>
            <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
              <QueryStatsOutlinedIcon color="primary" fontSize="small" />
              <Typography component="h2" sx={{ fontWeight: 800 }} variant="h6">
                Next-week court outlook
              </Typography>
            </Stack>
            <Typography color="text.secondary" variant="body2">
              Projection for the full UTC week beginning {targetLabel}
            </Typography>
          </Box>
          <Chip
            color={outlook.status === "validated" ? "success" : "default"}
            label={
              outlook.status === "validated"
                ? `${outlook.validation.confidence} backtest confidence`
                : "Not enough signal"
            }
            size="small"
            variant="outlined"
          />
        </Stack>

        <Box
          sx={{
            display: "grid",
            gap: { xs: 2, md: 3 },
            gridTemplateColumns: { xs: "1fr", md: "minmax(220px, .7fr) 1.3fr" },
          }}
        >
          <Box>
            <Typography
              sx={{ fontSize: { xs: "2.5rem", sm: "3rem" }, fontWeight: 850, lineHeight: 1 }}
            >
              {outlook.planning_reference_confirmed_court_events}
            </Typography>
            <Typography sx={{ fontWeight: 700 }} variant="subtitle2">
              {outlook.status === "validated"
                ? "court-event planning reference"
                : "currently confirmed for the target week"}
            </Typography>
            <Typography color="text.secondary" variant="body2">
              {outlook.status === "validated" &&
              outlook.historical_baseline_confirmed_court_events != null
                ? `${outlook.currently_confirmed_target_week_events} already confirmed · ${outlook.historical_baseline_confirmed_court_events} historical baseline (${outlook.baseline_lower_bound_confirmed_court_events}–${outlook.baseline_upper_bound_confirmed_court_events} recent-error band).`
                : "This is the known commitment count; no additional demand was guessed."}
            </Typography>
          </Box>
          <Box>
            <Typography color="text.secondary" variant="caption">
              Last eight completed weeks
            </Typography>
            <Box
              aria-label="Recorded confirmed court events for the last eight completed weeks"
              sx={{
                alignItems: "end",
                display: "grid",
                gap: 0.75,
                gridTemplateColumns: `repeat(${recent.length}, minmax(0, 1fr))`,
                height: 92,
                mt: 1,
              }}
            >
              {recent.map((week) => (
                <Box
                  key={week.start}
                  title={`${week.confirmed_court_events} confirmed court events`}
                  sx={{
                    bgcolor: "primary.main",
                    borderRadius: "4px 4px 1px 1px",
                    minHeight: 4,
                    opacity: 0.76,
                    height: `${Math.max(5, (week.confirmed_court_events / maximum) * 100)}%`,
                  }}
                />
              ))}
            </Box>
          </Box>
        </Box>

        {outlook.status !== "validated" ? (
          <Alert severity="info" icon={false}>
            No projection is shown because the rolling backtest did not meet the
            minimum volume and accuracy standard. Use confirmed commitments and
            recorded history without assuming additional demand.
          </Alert>
        ) : null}

        <Divider />
        <Box>
          <Typography sx={{ fontWeight: 700 }} variant="subtitle2">
            How to use this
          </Typography>
          <Typography color="text.secondary" variant="body2">
            {outlook.decision_supported} {outlook.validation.explanation}
          </Typography>
          <Typography color="text.secondary" sx={{ mt: 0.5 }} variant="caption">
            {outlook.validation.method} · {outlook.validation.backtest_weeks} backtest
            weeks
            {outlook.validation.weighted_absolute_percentage_error != null
              ? ` · ${outlook.validation.weighted_absolute_percentage_error}% weighted error`
              : ""}
          </Typography>
        </Box>
      </Stack>
    </Paper>
  );
}

export function OperationalInsightsWorkspace() {
  const { user } = useAuth();
  const orgView = useMyOrg();
  const {
    facility,
    facilityId,
    isLoading: facilityLoading,
  } = useResolvedFacility();
  const [focusStart, setFocusStart] = useState(() => utcDateBack(7));
  const [focusEnd, setFocusEnd] = useState(() => utcDateBack(1));
  const [referenceStart, setReferenceStart] = useState(() => utcDateBack(14));
  const [referenceEnd, setReferenceEnd] = useState(() => utcDateBack(8));
  const [result, setResult] =
    useState<OperationalInsightsComparisonResponse | null>(null);
  const [outlook, setOutlook] =
    useState<OperationalInsightsDemandOutlookResponse | null>(null);
  const [outlookError, setOutlookError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<{
    kind: "unavailable" | "forbidden" | "invalid" | "currency" | "retry";
    message: string;
  } | null>(null);
  const requestGeneration = useRef(0);
  const activeFacilityId = useRef(facilityId);
  activeFacilityId.current = facilityId;
  const organizationOwner = Boolean(
    user?.uid && orgView.org?.owner_user_id === user.uid,
  );

  useEffect(() => {
    requestGeneration.current += 1;
    setResult(null);
    setOutlook(null);
    setOutlookError(null);
    setError(null);
    setLoading(false);
  }, [facilityId]);

  const compare = async () => {
    if (!facilityId || loading) return;
    const requestFacilityId = facilityId;
    const generation = ++requestGeneration.current;
    const isCurrentRequest = () =>
      requestGeneration.current === generation &&
      activeFacilityId.current === requestFacilityId;
    const request: OperationalInsightsComparisonRequest = {
      focus: {
        label: "Recent period",
        start: asInstant(focusStart),
        end: afterInclusiveDate(focusEnd),
      },
      reference: {
        label: "Comparison period",
        start: asInstant(referenceStart),
        end: afterInclusiveDate(referenceEnd),
      },
    };
    setLoading(true);
    setError(null);
    setOutlook(null);
    setOutlookError(null);
    try {
      const [comparisonOutcome, outlookOutcome] = await Promise.allSettled([
        compareFacilityOperatingPeriods(requestFacilityId, request),
        getFacilityDemandOutlook(requestFacilityId),
      ]);
      if (!isCurrentRequest()) return;
      if (outlookOutcome.status === "fulfilled") {
        if (outlookOutcome.value.facility_id === requestFacilityId) {
          setOutlook(outlookOutcome.value);
        } else {
          setOutlook(null);
          setOutlookError(
            "The facility changed while the outlook was loading. Try again for the selected facility.",
          );
        }
      } else {
        setOutlook(null);
        if (
          comparisonOutcome.status === "fulfilled" ||
          !isApiError(outlookOutcome.reason) ||
          outlookOutcome.reason.status !== 503
        ) {
          setOutlookError(
            "The comparison is ready, but the demand outlook could not be refreshed. Try again shortly.",
          );
        }
      }
      if (comparisonOutcome.status === "rejected") {
        throw comparisonOutcome.reason;
      }
      const response = comparisonOutcome.value;
      if (response.facility_id !== requestFacilityId) {
        setResult(null);
        setError({
          kind: "retry",
          message:
            "The facility changed while this comparison was loading. Try again for the selected facility.",
        });
        return;
      }
      setResult(response);
    } catch (cause) {
      if (!isCurrentRequest()) return;
      setResult(null);
      if (isApiError(cause) && cause.status === 503) {
        setError({
          kind: "unavailable",
          message:
            "Planning insights aren’t enabled for this organization yet. Baseline revenue, bookings, payouts, receipts, and exports remain available.",
        });
      } else if (isApiError(cause) && cause.status === 403) {
        setError({
          kind: "forbidden",
          message:
            "You don’t have permission to view internal facility insights.",
        });
      } else if (isApiError(cause) && cause.status === 422) {
        setError({
          kind: "invalid",
          message:
            "Choose two completed, non-overlapping periods with the same number of days.",
        });
      } else if (
        isApiError(cause) &&
        cause.code === "ANALYTICS_CURRENCY_CONFLICT"
      ) {
        setError({
          kind: "currency",
          message:
            "One period contains multiple currencies, so a single revenue comparison would be misleading. Use Revenue or the transaction export for per-currency details.",
        });
      } else {
        setError({
          kind: "retry",
          message:
            "We couldn’t build this comparison. Review the periods and try again.",
        });
      }
    } finally {
      if (isCurrentRequest()) setLoading(false);
    }
  };

  if (facilityLoading || !facilityId) {
    return (
      <Stack
        aria-label="Loading planning insights"
        role="status"
        spacing={1}
        sx={{
          alignItems: "center",
          justifyContent: "center",
          minHeight: "40vh",
        }}
      >
        <CircularProgress aria-hidden size={24} />
        <Typography color="text.secondary" variant="body2">
          Loading…
        </Typography>
      </Stack>
    );
  }

  return (
    <Stack spacing={2.5} sx={{ px: { xs: 2, lg: 3 }, pb: 3 }}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={1.5}
        sx={{ alignItems: { sm: "center" }, justifyContent: "space-between" }}
      >
        <Box>
          <Typography component="h1" sx={{ fontWeight: 800 }} variant="h5">
            Planning insights
          </Typography>
          <Typography color="text.secondary" variant="body2">
            Compare recorded performance and review a validated demand outlook at{" "}
            {facility?.name ?? "this facility"}.
          </Typography>
        </Box>
        <Chip
          color="primary"
          icon={<InsightsOutlinedIcon />}
          label="Insights module"
          variant="outlined"
        />
      </Stack>

      <Paper
        component="form"
        variant="outlined"
        sx={{ p: { xs: 2, sm: 2.5 } }}
        onSubmit={(event) => {
          event.preventDefault();
          void compare();
        }}
      >
        <Stack spacing={2}>
          <Box>
            <Typography component="h2" sx={{ fontWeight: 700 }} variant="h6">
              Select equal periods
            </Typography>
            <Typography color="text.secondary" variant="body2">
              Completed UTC dates only, up to 92 days per period. Both start and
              last-day selections are included; periods cannot overlap.
            </Typography>
          </Box>
          <Box
            sx={{
              display: "grid",
              gap: 2,
              gridTemplateColumns: {
                xs: "1fr",
                md: "repeat(2, minmax(0, 1fr))",
              },
            }}
          >
            <Stack spacing={1.5}>
              <Typography sx={{ fontWeight: 700 }} variant="subtitle2">
                Recent period
              </Typography>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
                <TextField
                  {...dateProps}
                  label="Recent start"
                  value={focusStart}
                  onChange={(event) => setFocusStart(event.target.value)}
                />
                <TextField
                  {...dateProps}
                  label="Recent last day"
                  value={focusEnd}
                  onChange={(event) => setFocusEnd(event.target.value)}
                />
              </Stack>
            </Stack>
            <Stack spacing={1.5}>
              <Typography sx={{ fontWeight: 700 }} variant="subtitle2">
                Comparison period
              </Typography>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
                <TextField
                  {...dateProps}
                  label="Comparison start"
                  value={referenceStart}
                  onChange={(event) => setReferenceStart(event.target.value)}
                />
                <TextField
                  {...dateProps}
                  label="Comparison last day"
                  value={referenceEnd}
                  onChange={(event) => setReferenceEnd(event.target.value)}
                />
              </Stack>
            </Stack>
          </Box>
          <Button
            disabled={loading}
            type="submit"
            variant="contained"
            sx={{ alignSelf: "flex-start" }}
          >
            {loading ? "Building planning view…" : "Build planning view"}
          </Button>
        </Stack>
      </Paper>

      {error ? (
        <Alert
          severity={
            error.kind === "unavailable" || error.kind === "currency"
              ? "info"
              : "error"
          }
          action={
            error.kind === "unavailable" && organizationOwner ? (
              <Button
                component={RouterLink}
                color="inherit"
                size="small"
                to="/settings/contract"
              >
                Review modules
              </Button>
            ) : error.kind === "currency" ? (
              <Button
                color="inherit"
                component={RouterLink}
                size="small"
                to="/analytics/revenue"
              >
                Open revenue
              </Button>
            ) : error.kind === "retry" ? (
              <Button
                color="inherit"
                onClick={() => void compare()}
                size="small"
              >
                Retry
              </Button>
            ) : undefined
          }
        >
          {error.message}
        </Alert>
      ) : null}

      {outlookError && !error ? (
        <Alert severity="warning">{outlookError}</Alert>
      ) : null}

      {outlook?.facility_id === facilityId ? (
        <DemandOutlookCard outlook={outlook} />
      ) : null}

      {result?.facility_id === facilityId ? (
        <Stack spacing={2}>
          <Paper component="section" variant="outlined" sx={{ p: 2.5 }}>
            <Stack spacing={1.5}>
              <Box>
                <Typography
                  component="h2"
                  sx={{ fontWeight: 800 }}
                  variant="h6"
                >
                  What changed
                </Typography>
                <Typography color="text.secondary" variant="body2">
                  {result.decision_supported}
                </Typography>
              </Box>
              <Box
                sx={{
                  display: "grid",
                  gap: 2,
                  gridTemplateColumns: {
                    xs: "repeat(2, minmax(0, 1fr))",
                    md: "repeat(4, minmax(0, 1fr))",
                  },
                }}
              >
                <Metric
                  label="Bookings"
                  value={signed(result.change.booking_count_delta)}
                />
                <Metric
                  label="Utilization"
                  value={signed(
                    result.change.utilization_percentage_point_delta,
                    " pp",
                  )}
                />
                <Metric
                  label="Cancellations"
                  value={signed(
                    result.change.cancellation_percentage_point_delta,
                    " pp",
                  )}
                />
                <Metric
                  label="Gross revenue"
                  value={
                    result.change.revenue_comparable
                      ? money(
                          result.change.gross_revenue_delta,
                          result.change.currency,
                        )
                      : "Not comparable"
                  }
                />
              </Box>
            </Stack>
          </Paper>

          <Box
            sx={{
              display: "grid",
              gap: 2,
              gridTemplateColumns: {
                xs: "1fr",
                lg: "repeat(2, minmax(0, 1fr))",
              },
            }}
          >
            <PeriodCard period={result.focus} />
            <PeriodCard period={result.reference} />
          </Box>

          <Alert severity="info" icon={false}>
            <Typography sx={{ fontWeight: 700 }} variant="subtitle2">
              Freshness and limits
            </Typography>
            <Typography variant="body2">{result.freshness}</Typography>
            {result.limitations.map((limitation) => (
              <Typography key={limitation} variant="body2">
                • {limitation}
              </Typography>
            ))}
          </Alert>
        </Stack>
      ) : null}
    </Stack>
  );
}
